import { useEffect, useMemo, useState } from 'react';
import { documentoDeMiembro, rolesDelDocumento, type CanonicalDocument } from '@besbello/design-core';
import { validateDesign, type EffectiveZone, type TechniqueRuleVersion } from '@besbello/rules-core';
import { api } from '../api/client.js';
import { useT } from '../i18n/index.js';

/**
 * EL ENCARGO POR LOTE: la lista del club, pegada tal cual.
 *
 * Un equipo son dieciocho camisetas iguales salvo nombre y dorsal. Este panel
 * recibe la lista pegada («Aitor 10 M», una línea por persona, o con comas o
 * punto y coma), la enseña como tabla y VALIDA CADA MIEMBRO con el mismo motor
 * isomorfo del editor: «Etxebarria no cabe a este cuerpo» se sabe aquí, antes
 * de pagar, no en el taller.
 *
 * La sustitución de textos es `documentoDeMiembro`, la MISMA función que usará
 * producción: lo que este panel da por bueno es lo que se imprime.
 */

export interface MiembroFila {
  nombre: string;
  dorsal: string;
  size: string;
}

/** «Aitor Etxebarria 10 M» · «Aitor;10;M» · «Aitor, 10, M» → fila. */
export function parsearLineas(texto: string, tallaPorDefecto: string): MiembroFila[] {
  const filas: MiembroFila[] = [];
  for (const cruda of texto.split(/\r?\n/)) {
    const linea = cruda.trim();
    if (!linea) continue;

    const porSeparador = linea.split(/[;,\t]/).map((p) => p.trim());
    if (porSeparador.length >= 2) {
      filas.push({
        nombre: porSeparador[0] ?? '',
        dorsal: porSeparador[1] ?? '',
        size: porSeparador[2] || tallaPorDefecto,
      });
      continue;
    }

    // Sin separadores: las últimas palabras son dorsal y (quizá) talla.
    const partes = linea.split(/\s+/);
    if (partes.length >= 3 && /^\d{1,3}$/.test(partes[partes.length - 2] ?? '')) {
      filas.push({
        nombre: partes.slice(0, -2).join(' '),
        dorsal: partes[partes.length - 2] ?? '',
        size: partes[partes.length - 1] ?? tallaPorDefecto,
      });
    } else if (partes.length >= 2 && /^\d{1,3}$/.test(partes[partes.length - 1] ?? '')) {
      filas.push({ nombre: partes.slice(0, -1).join(' '), dorsal: partes[partes.length - 1] ?? '', size: tallaPorDefecto });
    } else {
      filas.push({ nombre: linea, dorsal: '', size: tallaPorDefecto });
    }
  }
  return filas;
}

export function PanelEquipo(props: {
  designId: string;
  document: CanonicalDocument;
  zone: EffectiveZone;
  technique: TechniqueRuleVersion;
  substrate: string;
  sizes: string[];
  onError: (mensaje: string) => void;
}) {
  const t = useT();
  const [texto, setTexto] = useState('');
  const [miembros, setMiembros] = useState<MiembroFila[]>([]);
  const [guardando, setGuardando] = useState(false);
  const [guardado, setGuardado] = useState(false);

  const roles = rolesDelDocumento(props.document);
  const tallaPorDefecto = props.sizes[0] ?? 'M';

  // La serie ya guardada, si la hay: se edita, no se empieza de cero.
  useEffect(() => {
    let vivo = true;
    void api
      .verSerie(props.designId)
      .then((r) => {
        if (vivo && r.members.length > 0) setMiembros(r.members);
      })
      .catch(() => {
        /* sin serie no pasa nada */
      });
    return () => {
      vivo = false;
    };
  }, [props.designId]);

  /*
    Validación POR MIEMBRO con el motor isomorfo. Es barato: el motor es puro,
    y dieciocho validaciones de un documento pequeño no se notan. El resultado
    se memoriza por lista+documento para no revalidar en cada render.
  */
  const problemas = useMemo(() => {
    const out = new Map<number, string>();
    for (const [i, m] of miembros.entries()) {
      try {
        const doc = documentoDeMiembro(props.document, m);
        const r = validateDesign(doc, {
          zone: props.zone,
          technique: props.technique,
          substrate: props.substrate,
          now: new Date().toISOString(),
        });
        const grave = r.diagnostics.find((d) => d.severity === 'error');
        if (grave) out.set(i, grave.code);
      } catch {
        /* una regla fuera de vigencia ya la señala el editor general */
      }
    }
    return out;
  }, [miembros, props.document, props.zone, props.technique, props.substrate]);

  const guardar = async () => {
    setGuardando(true);
    setGuardado(false);
    try {
      await api.ponerSerie(props.designId, { members: miembros });
      setGuardado(true);
    } catch (err) {
      props.onError((err as Error).message);
    } finally {
      setGuardando(false);
    }
  };

  return (
    <div className="space-y-3">
      {roles.length === 0 ? (
        <p data-test="equipo-sin-roles" className="rounded-lg border border-amber-300 bg-amber-50 p-3 text-sm">
          {t('equipo.sinRoles')}
        </p>
      ) : (
        <p className="text-xs text-tinta-suave">{t('equipo.ayuda')}</p>
      )}

      <textarea
        data-test="equipo-lista"
        value={texto}
        onChange={(e) => setTexto(e.target.value)}
        placeholder={'Aitor Etxebarria 10 M\nAna 7 S\nBrais; 23; L'}
        rows={5}
        className="w-full rounded-lg border border-borde p-2 font-mono text-xs"
      />
      <button
        data-test="equipo-cargar"
        onClick={() => setMiembros(parsearLineas(texto, tallaPorDefecto))}
        disabled={texto.trim().length === 0}
        className="toque rounded-lg border border-borde px-3 py-2 text-sm disabled:opacity-50"
      >
        {t('equipo.cargar')}
      </button>

      {miembros.length > 0 && (
        <>
          <table className="w-full text-sm" data-test="equipo-tabla">
            <thead>
              <tr className="text-left text-xs text-tinta-suave">
                <th className="py-1 pr-2">{t('equipo.nombre')}</th>
                <th className="py-1 pr-2">{t('equipo.dorsal')}</th>
                <th className="py-1 pr-2">{t('equipo.talla')}</th>
                <th className="py-1" aria-label={t('equipo.estado')} />
              </tr>
            </thead>
            <tbody>
              {miembros.map((m, i) => (
                <tr key={i} className="border-t border-borde">
                  <td className="py-1 pr-2">
                    <input
                      value={m.nombre}
                      onChange={(e) => setMiembros((v) => v.map((x, j) => (j === i ? { ...x, nombre: e.target.value } : x)))}
                      className="w-full rounded border border-borde px-1.5 py-1"
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <input
                      value={m.dorsal}
                      onChange={(e) => setMiembros((v) => v.map((x, j) => (j === i ? { ...x, dorsal: e.target.value } : x)))}
                      className="w-14 rounded border border-borde px-1.5 py-1"
                    />
                  </td>
                  <td className="py-1 pr-2">
                    <select
                      value={m.size}
                      onChange={(e) => setMiembros((v) => v.map((x, j) => (j === i ? { ...x, size: e.target.value } : x)))}
                      className="rounded border border-borde px-1.5 py-1"
                    >
                      {props.sizes.map((s) => (
                        <option key={s} value={s}>
                          {s}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="py-1 text-right">
                    {problemas.has(i) ? (
                      // El código del diagnóstico, no una traducción larga: la
                      // fila estrecha solo necesita señalar CUÁL falla.
                      <span data-test="equipo-problema" title={problemas.get(i)} className="text-xs font-medium text-red-700">
                        ⚠ {t('equipo.noCabe')}
                      </span>
                    ) : (
                      <span className="text-xs text-green-700">✓</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="flex items-center gap-2">
            <button
              data-test="equipo-guardar"
              onClick={() => void guardar()}
              disabled={guardando || miembros.length === 0 || roles.length === 0}
              className="toque rounded-lg bg-marca px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {t('equipo.guardar', { n: miembros.length })}
            </button>
            {guardado && <span className="text-xs text-green-700">{t('equipo.guardadoOk')}</span>}
            {problemas.size > 0 && (
              <span className="text-xs text-red-700">{t('equipo.conProblemas', { n: problemas.size })}</span>
            )}
          </div>
        </>
      )}
    </div>
  );
}
